import { useState, useEffect, useCallback } from "react";

const CLARO_RED  = "#E30613";
const CLARO_DARK = "#0f172a";
const CLARO_MUTED= "#64748b";
const CLARO_LIGHT= "#94a3b8";
const CLARO_BG   = "#f1f5f9";
const VERDE      = "#16a34a";

const MESES = [
  { value:"2026-01-01", label:"Enero 2026"   },
  { value:"2026-02-01", label:"Febrero 2026" },
  { value:"2026-03-01", label:"Marzo 2026"   },
  { value:"2026-04-01", label:"Abril 2026"   },
  { value:"2026-05-01", label:"Mayo 2026"    },
  { value:"2026-06-01", label:"Junio 2026"   },
  { value:"2026-07-01", label:"Julio 2026"   },
  { value:"2026-08-01", label:"Agosto 2026"  },
  { value:"2026-09-01", label:"Septiembre 2026" },
  { value:"2026-10-01", label:"Octubre 2026" },
  { value:"2026-11-01", label:"Noviembre 2026" },
  { value:"2026-12-01", label:"Diciembre 2026" },
];

function Card({ children, style }) {
  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px", marginBottom:"20px", ...style }}>
      {children}
    </div>
  );
}

function SectionTitle({ title, subtitle }) {
  return (
    <div style={{ marginBottom:"16px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"3px" }}>
        <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
        <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>{title}</div>
      </div>
      {subtitle && <div style={{ fontSize:"12px", color:CLARO_MUTED, marginLeft:"12px" }}>{subtitle}</div>}
    </div>
  );
}

function Input({ label, required, ...props }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
      <label style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.05em" }}>
        {label}{required && <span style={{ color:CLARO_RED }}> *</span>}
      </label>
      <input {...props} style={{
        padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px",
        fontSize:"13px", color:CLARO_DARK, outline:"none", background:"white", ...props.style
      }}
        onFocus={e => e.target.style.borderColor = CLARO_RED}
        onBlur={e  => e.target.style.borderColor = "#e2e8f0"}
      />
    </div>
  );
}

function Select({ label, required, children, ...props }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:"4px" }}>
      <label style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.05em" }}>
        {label}{required && <span style={{ color:CLARO_RED }}> *</span>}
      </label>
      <select {...props} style={{
        padding:"9px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px",
        fontSize:"13px", color:CLARO_DARK, outline:"none", background:"white", cursor:"pointer", ...props.style
      }}>
        {children}
      </select>
    </div>
  );
}

function Alert({ tipo, mensaje, onClose }) {
  const config = {
    success: { bg:"#f0fdf4", border:"#86efac", color:VERDE,   icon:"✅" },
    error:   { bg:"#fef2f2", border:"#fca5a5", color:"#991b1b", icon:"⚠️" },
    info:    { bg:"#eff6ff", border:"#93c5fd", color:"#1d4ed8", icon:"ℹ️" },
  }[tipo] || {};
  return (
    <div style={{ background:config.bg, border:`1px solid ${config.border}`, borderRadius:"8px", padding:"12px 16px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"12px" }}>
      <span style={{ color:config.color, fontSize:"13px" }}>{config.icon} {mensaje}</span>
      {onClose && <span onClick={onClose} style={{ cursor:"pointer", color:CLARO_LIGHT, fontWeight:"700", marginLeft:"12px" }}>✕</span>}
    </div>
  );
}

// ── Grilla tipo Excel: pegar varias filas a la vez (Ctrl+V) ────────
function emptyRowFor(columns) {
  return Object.fromEntries(columns.map(c => [c.key, ""]));
}

function PasteGrid({ columns, filas, setFilas, minRows = 6 }) {
  const addRow    = () => setFilas(prev => [...prev, emptyRowFor(columns)]);
  const removeRow = (idx) => setFilas(prev => prev.length <= 1 ? prev : prev.filter((_, i) => i !== idx));
  const limpiar   = () => setFilas(Array.from({ length: minRows }, () => emptyRowFor(columns)));

  const updateCell = (rowIdx, key, value) => {
    setFilas(prev => {
      const copy = prev.map(r => ({ ...r }));
      copy[rowIdx][key] = value;
      return copy;
    });
  };

  const handlePaste = (e, rowIdx, colIdx) => {
    const texto = e.clipboardData?.getData("text");
    if (!texto || (!texto.includes("\t") && !texto.includes("\n"))) return; // pegado simple: dejar comportamiento normal
    e.preventDefault();
    const filasPegadas = texto.replace(/\r/g, "").split("\n").filter((_, i, arr) => !(i === arr.length - 1 && arr[i] === ""));
    setFilas(prev => {
      let copy = prev.map(r => ({ ...r }));
      filasPegadas.forEach((textoFila, offsetFila) => {
        const celdas = textoFila.split("\t");
        const destinoFila = rowIdx + offsetFila;
        while (copy.length <= destinoFila) copy.push(emptyRowFor(columns));
        celdas.forEach((celda, offsetCol) => {
          const destinoCol = colIdx + offsetCol;
          if (destinoCol < columns.length) copy[destinoFila][columns[destinoCol].key] = celda.trim();
        });
      });
      return copy;
    });
  };

  const inputSt = {
    width: "100%", border: "none", outline: "none", background: "transparent",
    fontSize: "12px", padding: "6px 8px", color: CLARO_DARK, fontFamily: "inherit",
  };

  return (
    <div>
      <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
        <button onClick={addRow} style={{ padding: "6px 12px", background: CLARO_BG, border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "11px", fontWeight: "700", cursor: "pointer", color: CLARO_DARK }}>+ Agregar fila</button>
        <button onClick={limpiar} style={{ padding: "6px 12px", background: "white", border: "1px solid #e2e8f0", borderRadius: "6px", fontSize: "11px", cursor: "pointer", color: CLARO_MUTED }}>🗑 Limpiar todo</button>
      </div>
      <div style={{ overflowX: "auto", borderRadius: "8px", border: "1px solid #e2e8f0" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
          <thead>
            <tr>
              <th style={{ width: "30px", background: CLARO_BG, borderBottom: "1px solid #e2e8f0" }} />
              {columns.map(c => (
                <th key={c.key} style={{ padding: "8px 10px", textAlign: "left", fontSize: "11px", fontWeight: "700",
                  color: CLARO_MUTED, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0", background: CLARO_BG, whiteSpace: "nowrap" }}>
                  {c.label}{c.required && <span style={{ color: CLARO_RED }}> *</span>}
                </th>
              ))}
              <th style={{ width: "36px", background: CLARO_BG, borderBottom: "1px solid #e2e8f0" }} />
            </tr>
          </thead>
          <tbody>
            {filas.map((fila, rowIdx) => (
              <tr key={rowIdx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ textAlign: "center", color: CLARO_LIGHT, fontSize: "11px" }}>{rowIdx + 1}</td>
                {columns.map((c, colIdx) => (
                  <td key={c.key} style={{ borderLeft: "1px solid #f1f5f9", padding: 0 }}>
                    {c.type === "select" ? (
                      <select value={fila[c.key]} onChange={e => updateCell(rowIdx, c.key, e.target.value)}
                        onPaste={e => handlePaste(e, rowIdx, colIdx)}
                        style={{ ...inputSt, cursor: "pointer" }}>
                        <option value="">—</option>
                        {c.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : (
                      <input value={fila[c.key]} placeholder={c.placeholder || ""}
                        onChange={e => updateCell(rowIdx, c.key, e.target.value)}
                        onPaste={e => handlePaste(e, rowIdx, colIdx)}
                        style={inputSt} />
                    )}
                  </td>
                ))}
                <td style={{ textAlign: "center" }}>
                  <span onClick={() => removeRow(rowIdx)} style={{ cursor: "pointer", color: CLARO_LIGHT, fontSize: "13px" }}>✕</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ fontSize: "11px", color: CLARO_LIGHT, marginTop: "8px" }}>
        💡 Copia varias filas y columnas desde Excel y pégalas (Ctrl+V) sobre la primera celda — se distribuirán automáticamente.
      </div>
    </div>
  );
}

function GestionHuella({ usuario }) {
  const [resumen, setResumen]     = useState(null);
  const [tab, setTab]             = useState("excel"); // "excel" | "priorizacion" | "certificacion" | "gestionar"
  const [nodosRed, setNodosRed]   = useState([]);
  const [alerta, setAlerta]       = useState(null);

  // Excel
  const [archivo, setArchivo]     = useState(null);
  const [cargando, setCargando]   = useState(false);
  const [progreso, setProgreso]   = useState(null);
  const [backupInfo, setBackupInfo]           = useState(null);
  const [rollbackLoading, setRollbackLoading] = useState(false);

  // Form priorizacion
  const [fPrio, setFPrio] = useState({ mes_priorizacion:"", nodo:"", priorizacion_calidad:"", hhpp:"", tipo:"Priorizado" });
  const [loadPrio, setLoadPrio]   = useState(false);
  const [modoPrio, setModoPrio]   = useState("individual"); // "individual" | "lote"
  const columnasPrio = [
    { key:"mes_priorizacion",     label:"Mes Priorizacion", required:true, placeholder:"2026-03 o 01/03/2026" },
    { key:"nodo",                 label:"Nodo",             required:true, placeholder:"ID nodo" },
    { key:"priorizacion_calidad", label:"Calidad ICR",      placeholder:"Ej: 145" },
    { key:"hhpp",                 label:"HHPP",              placeholder:"Ej: 1200" },
    { key:"tipo",                 label:"Tipo", type:"select", options:["Priorizado","Garantia"] },
  ];
  const [filasPrioLote, setFilasPrioLote] = useState(() => Array.from({length:6}, () => emptyRowFor(columnasPrio)));
  const [loadPrioLote, setLoadPrioLote]   = useState(false);

  // Form certificacion
  const [fCert, setFCert] = useState({ nodo:"", prioridad:"", ot_chg:"", fecha_certificacion:"" });
  const [loadCert, setLoadCert]   = useState(false);
  const [modoCert, setModoCert]   = useState("individual"); // "individual" | "lote"
  const columnasCert = [
    { key:"nodo",                 label:"Nodo",              required:true, placeholder:"ID nodo" },
    { key:"fecha_certificacion",  label:"Fecha Certificacion", required:true, placeholder:"2026-03-15 o 15/03/2026" },
    { key:"ot_chg",               label:"OT / CHG",          placeholder:"Ej: OT4812838" },
    { key:"prioridad",            label:"Prioridad",         placeholder:"Ej: P1, P2..." },
  ];
  const [filasCertLote, setFilasCertLote] = useState(() => Array.from({length:6}, () => emptyRowFor(columnasCert)));
  const [loadCertLote, setLoadCertLote]   = useState(false);

  const API = process.env.REACT_APP_API_URL;

  const cargarResumen = useCallback(async () => {
    try {
      const r = await fetch(`${API}/huella-resumen-tablas`);
      setResumen(await r.json());
    } catch {}
  }, [API]);

  const cargarNodos = useCallback(async () => {
    try {
      const r = await fetch(`${API}/huella-nodos-red`);
      setNodosRed(await r.json());
    } catch {}
  }, [API]);

  const cargarBackupInfo = useCallback(async () => {
    try {
      const r = await fetch(`${API}/huella-backup-info`);
      setBackupInfo(await r.json());
    } catch {}
  }, [API]);

  useEffect(() => { cargarResumen(); cargarNodos(); cargarBackupInfo(); }, [cargarResumen, cargarNodos, cargarBackupInfo]);

  const mostrarAlerta = (tipo, mensaje) => {
    setAlerta({ tipo, mensaje });
    setTimeout(() => setAlerta(null), 6000);
  };

  // ── Subir Excel ────────────────────────────────────────────────
  const handleRollback = async () => {
    if (!window.confirm("¿Seguro que deseas revertir la última carga? Se restaurarán los datos anteriores.")) return;
    setRollbackLoading(true);
    try {
      const res  = await fetch(`${API}/huella-rollback`, { method:"POST" });
      const data = await res.json();
      if (data.ok) { mostrarAlerta("success", data.mensaje); cargarResumen(); cargarBackupInfo(); }
      else           mostrarAlerta("error", data.mensaje);
    } catch (e) { mostrarAlerta("error", e.message); }
    finally { setRollbackLoading(false); }
  };

  const [descargando, setDescargando] = useState(false);
  const handleDescargarTodo = async () => {
    setDescargando(true);
    try {
      const res = await fetch(`${API}/huella-exportar-excel`);
      if (!res.ok) throw new Error("No se pudo generar el archivo.");
      const blob = await res.blob();
      const url  = window.URL.createObjectURL(blob);
      const a    = document.createElement("a");
      const fecha = new Date().toISOString().slice(0,10);
      a.href = url;
      a.download = `ProyectoHuella_${fecha}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      mostrarAlerta("error", e.message);
    } finally {
      setDescargando(false);
    }
  };

  const handleExcel = async () => {
    if (!archivo) return mostrarAlerta("error", "Selecciona un archivo Excel primero.");
    setCargando(true); setProgreso(null);
    try {
      // Paso 1: crear backup antes de cargar
      setProgreso("Creando respaldo de seguridad...");
      await fetch(`${API}/huella-backup`, { method:"POST" });
      cargarBackupInfo();

      const form = new FormData();
      form.append("archivo", archivo);
      setProgreso("Procesando archivo Excel...");
      const res  = await fetch(`${API}/huella-carga-excel`, { method:"POST", body:form });
      const data = await res.json();
      if (data.ok) {
        const {
          priorizacion, priorizacion_actualizados, priorizacion_omitidos, priorizacion_eliminados,
          certificacion, certificacion_actualizados, certificacion_omitidos, certificacion_eliminados,
          metas, metas_actualizadas, metas_omitidas, metas_eliminadas,
          errores,
        } = data.resumen;
        const partes = [];
        if (priorizacion > 0)               partes.push(`✅ ${priorizacion} priorizaciones nuevas`);
        if (priorizacion_actualizados > 0)  partes.push(`🔄 ${priorizacion_actualizados} priorizaciones actualizadas`);
        if (priorizacion_eliminados > 0)    partes.push(`🗑 ${priorizacion_eliminados} priorizaciones eliminadas (ya no estaban en el Excel)`);
        if (priorizacion_omitidos > 0)      partes.push(`⏭ ${priorizacion_omitidos} priorizaciones omitidas (sin nodo o mes)`);
        if (certificacion > 0)              partes.push(`✅ ${certificacion} certificaciones nuevas`);
        if (certificacion_actualizados > 0) partes.push(`🔄 ${certificacion_actualizados} certificaciones actualizadas`);
        if (certificacion_eliminados > 0)   partes.push(`🗑 ${certificacion_eliminados} certificaciones eliminadas (ya no estaban en el Excel)`);
        if (certificacion_omitidos > 0)     partes.push(`⏭ ${certificacion_omitidos} certificaciones omitidas (sin nodo o fecha)`);
        if (metas > 0)                      partes.push(`✅ ${metas} metas nuevas`);
        if (metas_actualizadas > 0)         partes.push(`🔄 ${metas_actualizadas} metas actualizadas`);
        if (metas_eliminadas > 0)           partes.push(`🗑 ${metas_eliminadas} metas eliminadas (ya no estaban en el Excel)`);
        if (metas_omitidas > 0)             partes.push(`⏭ ${metas_omitidas} metas omitidas (sin mes)`);
        if (errores?.length)                partes.push(`⚠️ ${errores.length} errores`);
        mostrarAlerta("success", partes.join(" · ") || "Archivo procesado sin cambios nuevos.");
        setArchivo(null);
        document.getElementById("inputExcel").value = "";
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje || "Error al procesar el archivo.");
      }
    } catch (e) {
      mostrarAlerta("error", "Error de conexión: " + e.message);
    } finally {
      setCargando(false); setProgreso(null);
    }
  };

  // ── Agregar priorización manual ────────────────────────────────
  const handlePrio = async () => {
    if (!fPrio.mes_priorizacion || !fPrio.nodo) return mostrarAlerta("error", "Mes y Nodo son requeridos.");
    setLoadPrio(true);
    try {
      const res  = await fetch(`${API}/huella-agregar-priorizacion`, {
        method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify(fPrio)
      });
      const data = await res.json();
      if (data.ok) {
        mostrarAlerta("success", data.mensaje);
        setFPrio({ mes_priorizacion:"", nodo:"", priorizacion_calidad:"", hhpp:"", tipo:"Priorizado" });
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje);
      }
    } catch (e) { mostrarAlerta("error", e.message); }
    finally { setLoadPrio(false); }
  };

  // ── Agregar certificación manual ───────────────────────────────
  const handleCert = async () => {
    if (!fCert.nodo || !fCert.fecha_certificacion) return mostrarAlerta("error", "Nodo y Fecha son requeridos.");
    setLoadCert(true);
    try {
      const res  = await fetch(`${API}/huella-agregar-certificacion`, {
        method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify(fCert)
      });
      const data = await res.json();
      if (data.ok) {
        mostrarAlerta("success", data.mensaje);
        setFCert({ nodo:"", prioridad:"", ot_chg:"", fecha_certificacion:"" });
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje);
      }
    } catch (e) { mostrarAlerta("error", e.message); }
    finally { setLoadCert(false); }
  };

  const resumenLoteMsg = (r) => {
    const partes = [];
    if (r.agregados > 0) partes.push(`✅ ${r.agregados} agregados`);
    if (r.omitidos  > 0) partes.push(`⏭ ${r.omitidos} omitidos (ya existían)`);
    if (r.errores?.length) partes.push(`⚠️ ${r.errores.length} con error (fila ${r.errores.map(e=>e.fila).join(", ")})`);
    return partes.join(" · ") || "No se procesó ningún registro.";
  };

  // ── Agregar priorización masiva (pegado tipo Excel) ────────────
  const handlePrioLote = async () => {
    const filasValidas = filasPrioLote.filter(f => f.nodo?.trim() || f.mes_priorizacion?.trim());
    if (!filasValidas.length) return mostrarAlerta("error", "No hay filas con datos para cargar.");
    setLoadPrioLote(true);
    try {
      const res  = await fetch(`${API}/huella-agregar-priorizacion-lote`, {
        method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify({ filas: filasValidas })
      });
      const data = await res.json();
      if (data.ok) {
        mostrarAlerta(data.resumen.errores?.length ? "info" : "success", resumenLoteMsg(data.resumen));
        setFilasPrioLote(Array.from({length:6}, () => emptyRowFor(columnasPrio)));
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje || "Error al procesar las filas.");
      }
    } catch (e) { mostrarAlerta("error", e.message); }
    finally { setLoadPrioLote(false); }
  };

  // ── Agregar certificación masiva (pegado tipo Excel) ───────────
  const handleCertLote = async () => {
    const filasValidas = filasCertLote.filter(f => f.nodo?.trim() || f.fecha_certificacion?.trim());
    if (!filasValidas.length) return mostrarAlerta("error", "No hay filas con datos para cargar.");
    setLoadCertLote(true);
    try {
      const res  = await fetch(`${API}/huella-agregar-certificacion-lote`, {
        method:"POST", headers:{ "Content-Type":"application/json" }, body:JSON.stringify({ filas: filasValidas })
      });
      const data = await res.json();
      if (data.ok) {
        mostrarAlerta(data.resumen.errores?.length ? "info" : "success", resumenLoteMsg(data.resumen));
        setFilasCertLote(Array.from({length:6}, () => emptyRowFor(columnasCert)));
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje || "Error al procesar las filas.");
      }
    } catch (e) { mostrarAlerta("error", e.message); }
    finally { setLoadCertLote(false); }
  };

  const modoBtnStyle = (activo) => ({
    padding:"7px 16px", borderRadius:"8px", fontSize:"12px", fontWeight:"700",
    cursor:"pointer", border: activo ? "none" : "1px solid #e2e8f0",
    background: activo ? CLARO_DARK : "white",
    color:       activo ? "white"   : CLARO_MUTED,
  });

  const btnStyle = (activo) => ({
    padding:"9px 20px", borderRadius:"8px", fontSize:"13px", fontWeight:"600",
    cursor:"pointer", border:"none", transition:"all 0.15s",
    background: activo ? CLARO_RED : CLARO_BG,
    color:       activo ? "white"   : CLARO_MUTED,
    boxShadow:   activo ? `0 2px 8px ${CLARO_RED}40` : "none",
  });

  const submitBtn = (loading, label, onClick) => (
    <button onClick={onClick} disabled={loading}
      style={{ padding:"10px 24px", background: loading ? "#9ca3af" : `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`, color:"white", border:"none", borderRadius:"8px", fontSize:"13px", fontWeight:"700", cursor: loading ? "not-allowed" : "pointer", boxShadow: loading ? "none" : `0 4px 12px ${CLARO_RED}40` }}>
      {loading ? "⏳ Procesando..." : label}
    </button>
  );

  return (
    <div style={{ fontFamily:"'Segoe UI', Arial, sans-serif", color:CLARO_DARK }}>

      {/* Título */}
      <div style={{ marginBottom:"24px", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:"12px" }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"4px" }}>
            <div style={{ width:"4px", height:"22px", background:CLARO_RED, borderRadius:"2px" }} />
            <h2 style={{ margin:0, fontSize:"20px", fontWeight:"700", color:CLARO_DARK }}>Gestión de datos — Proyecto Huella</h2>
          </div>
          <p style={{ margin:"0 0 0 14px", fontSize:"13px", color:CLARO_MUTED }}>Carga masiva por Excel o ingreso manual de nodos priorizados y certificados</p>
        </div>
        <button onClick={handleDescargarTodo} disabled={descargando}
          style={{ padding:"10px 20px", background: descargando ? "#9ca3af" : VERDE, color:"white", border:"none",
            borderRadius:"8px", fontSize:"13px", fontWeight:"700", cursor: descargando ? "not-allowed" : "pointer",
            boxShadow: descargando ? "none" : `0 4px 12px ${VERDE}40`, display:"flex", alignItems:"center", gap:"8px", whiteSpace:"nowrap" }}>
          {descargando ? "⏳ Generando..." : "⬇ Descargar todo (Excel)"}
        </button>
      </div>

      {/* Alerta global */}
      {alerta && <Alert tipo={alerta.tipo} mensaje={alerta.mensaje} onClose={() => setAlerta(null)} />}

      {/* Resumen estado actual */}
      {resumen && (
        <div style={{ display:"flex", gap:"12px", flexWrap:"wrap", marginBottom:"24px" }}>
          {[
            { label:"Nodos priorizados",   valor:resumen.priorizacion,  color:CLARO_DARK },
            { label:"Certificaciones",      valor:resumen.certificacion, color:VERDE },
            { label:"Registros de metas",   valor:resumen.metas,         color:"#2563eb" },
          ].map(({ label, valor, color }) => (
            <div key={label} style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"14px 20px", minWidth:"140px" }}>
              <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"6px" }}>{label}</div>
              <div style={{ fontSize:"26px", fontWeight:"700", color }}>{valor}</div>
              <div style={{ fontSize:"11px", color:CLARO_LIGHT, marginTop:"2px" }}>en base de datos</div>
            </div>
          ))}
          {/* Registros por mes */}
          {resumen.por_mes?.length > 0 && (
            <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"14px 20px", flex:1, minWidth:"200px" }}>
              <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.07em", marginBottom:"8px" }}>Nodos por mes</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:"6px" }}>
                {resumen.por_mes.map(m => (
                  <span key={m.mes} style={{ background:CLARO_BG, borderRadius:"6px", padding:"3px 10px", fontSize:"12px", color:CLARO_DARK, fontWeight:"600" }}>
                    {m.mes}: <strong style={{ color:CLARO_RED }}>{m.total}</strong>
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display:"flex", gap:"8px", marginBottom:"20px" }}>
        <button style={btnStyle(tab==="excel")}        onClick={()=>setTab("excel")}>📂 Cargar Excel</button>
        <button style={btnStyle(tab==="priorizacion")} onClick={()=>setTab("priorizacion")}>➕ Agregar priorización</button>
        <button style={btnStyle(tab==="certificacion")}onClick={()=>setTab("certificacion")}>✅ Agregar certificación</button>
        <button style={btnStyle(tab==="gestionar")}    onClick={()=>setTab("gestionar")}>🗂 Gestionar registros</button>
        <button style={btnStyle(tab==="papelera")}     onClick={()=>setTab("papelera")}>🗑 Papelera</button>
      </div>

      {/* ── TAB EXCEL ── */}
      {tab === "excel" && (
        <Card>
          <SectionTitle
            title="Carga masiva desde Excel"
            subtitle="El archivo debe tener las hojas: BD_Priorizacion, BD_Certificacion_Nodos y BD_Metas_Huella con el mismo formato original."
          />

          {/* Guía de formato */}
          <div style={{ background:CLARO_BG, borderRadius:"8px", padding:"14px 16px", marginBottom:"20px" }}>
            <div style={{ fontSize:"12px", fontWeight:"700", color:CLARO_DARK, marginBottom:"10px" }}>📋 Formato esperado por hoja:</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:"12px" }}>
              {[
                { hoja:"BD_Priorizacion",        cols:["Mes Priorizacion","Nodo","Priorización Calidad","HHPP","Tipo (Priorizado/Garantia)"] },
                { hoja:"BD_Certificacion_Nodos", cols:["NODO","Prioridad","OT/CHG","Fecha Certificacion"] },
                { hoja:"BD_Metas_Huella",        cols:["Mes Priorizacion","Meta","Aliado"] },
              ].map(({ hoja, cols }) => (
                <div key={hoja} style={{ background:"white", borderRadius:"6px", padding:"10px 12px", border:"1px solid #e2e8f0" }}>
                  <div style={{ fontSize:"11px", fontWeight:"700", color:CLARO_RED, marginBottom:"6px" }}>{hoja}</div>
                  {cols.map(c => <div key={c} style={{ fontSize:"11px", color:CLARO_MUTED, padding:"1px 0" }}>• {c}</div>)}
                </div>
              ))}
            </div>
          </div>

          {/* Selector de archivo */}
          <div style={{ border:"2px dashed #e2e8f0", borderRadius:"10px", padding:"32px", textAlign:"center", marginBottom:"16px", background:archivo?"#f0fdf4":"white", borderColor:archivo?"#86efac":"#e2e8f0" }}>
            <div style={{ fontSize:"32px", marginBottom:"8px" }}>{archivo ? "📊" : "📂"}</div>
            <div style={{ fontSize:"14px", fontWeight:"600", color:CLARO_DARK, marginBottom:"4px" }}>
              {archivo ? archivo.name : "Selecciona o arrastra tu archivo Excel"}
            </div>
            <div style={{ fontSize:"12px", color:CLARO_LIGHT, marginBottom:"16px" }}>
              {archivo ? `${(archivo.size/1024).toFixed(1)} KB` : "Formatos: .xlsx, .xls"}
            </div>
            <input id="inputExcel" type="file" accept=".xlsx,.xls"
              onChange={e => setArchivo(e.target.files[0] || null)}
              style={{ display:"none" }} />
            <button onClick={() => document.getElementById("inputExcel").click()}
              style={{ padding:"9px 20px", background:CLARO_BG, border:`1px solid #e2e8f0`, borderRadius:"8px", fontSize:"13px", fontWeight:"600", cursor:"pointer", color:CLARO_DARK }}>
              {archivo ? "Cambiar archivo" : "Seleccionar archivo"}
            </button>
          </div>

          {/* Backup disponible */}
          {backupInfo?.disponible && (
            <div style={{ background:"#fff7ed", border:"1px solid #fed7aa", borderRadius:"8px", padding:"12px 16px", marginBottom:"12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <span style={{ fontSize:"12px", color:"#92400e" }}>
                🔄 Hay un respaldo disponible con <strong>{backupInfo.total}</strong> registros de priorización.
              </span>
              <button onClick={handleRollback} disabled={rollbackLoading}
                style={{ padding:"7px 16px", background: rollbackLoading ? "#9ca3af" : "#f97316", color:"white", border:"none", borderRadius:"8px", fontSize:"12px", fontWeight:"700", cursor: rollbackLoading ? "not-allowed" : "pointer" }}>
                {rollbackLoading ? "⏳ Revirtiendo..." : "↩ Revertir carga"}
              </button>
            </div>
          )}

          {progreso && <Alert tipo="info" mensaje={progreso} />}

          <div style={{ display:"flex", gap:"10px", alignItems:"center" }}>
            {submitBtn(cargando, "📤 Procesar y cargar Excel", handleExcel)}
            {archivo && (
              <button onClick={() => { setArchivo(null); document.getElementById("inputExcel").value=""; }}
                style={{ padding:"9px 16px", background:"white", border:"1px solid #e2e8f0", borderRadius:"8px", fontSize:"13px", cursor:"pointer", color:CLARO_MUTED }}>
                Cancelar
              </button>
            )}
          </div>
        </Card>
      )}

      {/* ── TAB PRIORIZACION ── */}
      {tab === "priorizacion" && (
        <Card>
          <SectionTitle
            title="Agregar nodo a priorización"
            subtitle={modoPrio === "individual"
              ? "Registra un nodo individual en la tabla de priorización del mes."
              : "Pega varias filas a la vez (desde Excel) para cargar varios nodos de una sola vez."}
          />
          <div style={{ display:"flex", gap:"8px", marginBottom:"20px" }}>
            <button style={modoBtnStyle(modoPrio==="individual")} onClick={()=>setModoPrio("individual")}>👤 Uno a uno</button>
            <button style={modoBtnStyle(modoPrio==="lote")} onClick={()=>setModoPrio("lote")}>📋 Varios registros (pegar)</button>
          </div>

          {modoPrio === "lote" ? (
            <>
              <PasteGrid columns={columnasPrio} filas={filasPrioLote} setFilas={setFilasPrioLote} />
              <div style={{ marginTop:"18px" }}>
                {submitBtn(loadPrioLote, `📤 Cargar ${filasPrioLote.filter(f=>f.nodo?.trim()||f.mes_priorizacion?.trim()).length} registro(s)`, handlePrioLote)}
              </div>
            </>
          ) : (
          <>
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:"16px", marginBottom:"20px" }}>
            <Select label="Mes de priorización" required value={fPrio.mes_priorizacion} onChange={e=>setFPrio({...fPrio,mes_priorizacion:e.target.value})}>
              <option value="">Selecciona un mes</option>
              {MESES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
            </Select>
            <Select label="Nodo" required value={fPrio.nodo} onChange={e=>setFPrio({...fPrio,nodo:e.target.value})}>
              <option value="">Selecciona un nodo</option>
              {nodosRed.map(n => (
                <option key={n.id_nodo} value={n.id_nodo}>{n.id_nodo} — {n.NOMBRE_NODO || n.id_nodo}</option>
              ))}
            </Select>
            <Input label="Calidad ICR (priorización)" type="number" placeholder="Ej: 145" value={fPrio.priorizacion_calidad} onChange={e=>setFPrio({...fPrio,priorizacion_calidad:e.target.value})} />
            <Input label="HHPP" type="number" placeholder="Ej: 1200" value={fPrio.hhpp} onChange={e=>setFPrio({...fPrio,hhpp:e.target.value})} />
            <Select label="Tipo de priorización" required value={fPrio.tipo} onChange={e=>setFPrio({...fPrio,tipo:e.target.value})}>
              <option value="Priorizado">🔵 Priorizado — priorización estándar del mes</option>
              <option value="Garantia">🟡 Garantía — nodo con obligación de garantía</option>
            </Select>
          </div>
          {/* Preview */}
          {fPrio.nodo && (
            <div style={{ background:CLARO_BG, borderRadius:"8px", padding:"12px 16px", marginBottom:"16px", fontSize:"12px", color:CLARO_MUTED }}>
              {(() => {
                const n = nodosRed.find(x => x.id_nodo === fPrio.nodo);
                return n ? `📍 ${n.NOMBRE_NODO || n.id_nodo} — ${n.MUNICIPIO || "sin ciudad"} — Aliado: ${n.Aliado_Rexterna || "—"}` : "";
              })()}
            </div>
          )}
          {/* Indicador tipo seleccionado */}
          <div style={{ marginBottom:"16px", display:"flex", alignItems:"center", gap:"10px" }}>
            <span style={{
              display:"inline-block", padding:"5px 14px", borderRadius:"20px", fontSize:"12px", fontWeight:"700",
              background: fPrio.tipo === "Garantia" ? "#fefce8" : "#eff6ff",
              color:       fPrio.tipo === "Garantia" ? "#854d0e"  : "#1d4ed8",
              border:      fPrio.tipo === "Garantia" ? "1px solid #fde047" : "1px solid #93c5fd",
            }}>
              {fPrio.tipo === "Garantia" ? "🟡 Garantía" : "🔵 Priorizado"}
            </span>
            <span style={{ fontSize:"11px", color:"#94a3b8" }}>
              {fPrio.tipo === "Garantia"
                ? "Este nodo será catalogado como garantía — puede coexistir con la priorización normal del mismo mes"
                : "Priorización estándar del mes"}
            </span>
          </div>
          {submitBtn(loadPrio, "➕ Agregar nodo priorizado", handlePrio)}
          </>
          )}
        </Card>
      )}

      {/* ── TAB CERTIFICACION ── */}
      {tab === "certificacion" && (
        <Card>
          <SectionTitle
            title="Registrar certificación de nodo"
            subtitle={modoCert === "individual"
              ? "Marca un nodo como certificado. Regla: un nodo solo puede certificarse una vez por mes."
              : "Pega varias filas a la vez (desde Excel) para certificar varios nodos de una sola vez."}
          />
          <div style={{ display:"flex", gap:"8px", marginBottom:"20px" }}>
            <button style={modoBtnStyle(modoCert==="individual")} onClick={()=>setModoCert("individual")}>👤 Uno a uno</button>
            <button style={modoBtnStyle(modoCert==="lote")} onClick={()=>setModoCert("lote")}>📋 Varios registros (pegar)</button>
          </div>

          {modoCert === "lote" ? (
            <>
              <PasteGrid columns={columnasCert} filas={filasCertLote} setFilas={setFilasCertLote} />
              <div style={{ marginTop:"18px" }}>
                {submitBtn(loadCertLote, `📤 Cargar ${filasCertLote.filter(f=>f.nodo?.trim()||f.fecha_certificacion?.trim()).length} registro(s)`, handleCertLote)}
              </div>
            </>
          ) : (
          <>
          {/* Advertencia si ya existe cert en ese mes */}
          {fCert.nodo && fCert.fecha_certificacion && (
            <div style={{ background:"#fffbeb", border:"1px solid #fde68a", borderRadius:"8px",
              padding:"10px 14px", marginBottom:"14px", fontSize:"12px", color:"#92400e" }}>
              ⚠️ Si ya existe una certificación para <strong>{fCert.nodo}</strong> en <strong>
              {fCert.fecha_certificacion.slice(0,7)}</strong>, el sistema la rechazará automáticamente.
            </div>
          )}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(2,1fr)", gap:"16px", marginBottom:"20px" }}>
            <Select label="Nodo" required value={fCert.nodo} onChange={e=>setFCert({...fCert,nodo:e.target.value})}>
              <option value="">Selecciona un nodo</option>
              {nodosRed.map(n => (
                <option key={n.id_nodo} value={n.id_nodo}>{n.id_nodo} — {n.NOMBRE_NODO || n.id_nodo}</option>
              ))}
            </Select>
            <Input label="Fecha de certificación" required type="date" value={fCert.fecha_certificacion} onChange={e=>setFCert({...fCert,fecha_certificacion:e.target.value})} />
            <Input label="OT / CHG" placeholder="Ej: OT4812838" value={fCert.ot_chg} onChange={e=>setFCert({...fCert,ot_chg:e.target.value})} />
            <Input label="Prioridad" placeholder="Ej: P1, P2, Priorizado Marzo..." value={fCert.prioridad} onChange={e=>setFCert({...fCert,prioridad:e.target.value})} />
          </div>
          {/* Preview nodo */}
          {fCert.nodo && (
            <div style={{ background:CLARO_BG, borderRadius:"8px", padding:"12px 16px", marginBottom:"16px", fontSize:"12px", color:CLARO_MUTED }}>
              {(() => {
                const n = nodosRed.find(x => x.id_nodo === fCert.nodo);
                return n ? `📍 ${n.NOMBRE_NODO || n.id_nodo} — ${n.MUNICIPIO || "sin ciudad"} — Aliado: ${n.Aliado_Rexterna || "—"}` : "";
              })()}
            </div>
          )}
          {submitBtn(loadCert, "✅ Registrar certificación", handleCert)}
          </>
          )}
        </Card>
      )}

      {/* ── TAB GESTIONAR ── */}
      {tab === "gestionar" && (
        <TabGestionar API={API} mostrarAlerta={mostrarAlerta} cargarResumen={cargarResumen} nodosRed={nodosRed} MESES={MESES} />
      )}

      {/* ── TAB PAPELERA ── */}
      {tab === "papelera" && (
        <TabPapelera API={API} mostrarAlerta={mostrarAlerta} cargarResumen={cargarResumen} />
      )}

    </div>
  );
}

function TabGestionar({ API, mostrarAlerta, cargarResumen, nodosRed, MESES }) {
  const [vista, setVista]           = useState("priorizacion"); // "priorizacion" | "certificacion"
  const [filas, setFilas]           = useState([]);
  const [cargando, setCargando]     = useState(false);
  const [seleccionados, setSeleccionados] = useState([]);
  const [editando, setEditando]     = useState(null); // { id, campo: valor }
  const [filtroMes, setFiltroMes]   = useState("");
  const [filtroTipo, setFiltroTipo] = useState("");
  const [busqueda, setBusqueda]     = useState("");
  const [eliminando, setEliminando] = useState(false);

  const cargar = async () => {
    setCargando(true); setSeleccionados([]); setEditando(null);
    try {
      const params = new URLSearchParams();
      if (filtroMes)  params.append("mes",      filtroMes);
      if (filtroTipo) params.append("tipo",      filtroTipo);
      if (busqueda)   params.append("busqueda",  busqueda);
      const endpoint = vista === "priorizacion" ? "huella-listar-priorizacion" : "huella-listar-certificacion";
      const res  = await fetch(`${API}/${endpoint}?${params}`);
      setFilas(await res.json());
    } catch { setFilas([]); }
    finally { setCargando(false); }
  };

  useEffect(() => { cargar(); }, [vista, filtroMes, filtroTipo, busqueda]);

  const toggleSel = (id) => setSeleccionados(s => s.includes(id) ? s.filter(x=>x!==id) : [...s, id]);
  const toggleTodos = () => setSeleccionados(s => s.length === filas.length ? [] : filas.map(f=>f.id));

  const handleEliminar = async () => {
    if (!seleccionados.length) return mostrarAlerta("error", "Selecciona al menos un registro.");
    if (!window.confirm(`¿Eliminar ${seleccionados.length} registro(s)? Esta acción no se puede deshacer.`)) return;
    setEliminando(true);
    try {
      const endpoint = vista === "priorizacion" ? "huella-eliminar-priorizacion" : "huella-eliminar-certificacion";
      const res  = await fetch(`${API}/${endpoint}`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids: seleccionados })
      });
      const data = await res.json();
      if (data.ok) { mostrarAlerta("success", data.mensaje); cargar(); cargarResumen(); }
      else           mostrarAlerta("error", data.mensaje);
    } catch(e) { mostrarAlerta("error", e.message); }
    finally { setEliminando(false); }
  };

  const handleGuardarEdicion = async () => {
    if (!editando) return;
    const endpoint = vista === "priorizacion"
      ? `huella-editar-priorizacion/${editando.id}`
      : `huella-editar-certificacion/${editando.id}`;
    try {
      const res  = await fetch(`${API}/${endpoint}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editando)
      });
      const data = await res.json();
      if (data.ok) { mostrarAlerta("success", data.mensaje); setEditando(null); cargar(); }
      else           mostrarAlerta("error", data.mensaje);
    } catch(e) { mostrarAlerta("error", e.message); }
  };

  const thSt = { padding:"8px 12px", textAlign:"left", fontSize:"11px", fontWeight:"700",
    color:"#64748b", textTransform:"uppercase", borderBottom:"1px solid #e2e8f0",
    background:"#f1f5f9", whiteSpace:"nowrap" };
  const tdSt = { padding:"9px 12px", fontSize:"12px", color:"#0f172a", borderBottom:"1px solid #f1f5f9" };

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px" }}>
      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"16px" }}>
        <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
        <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>Gestionar registros</div>
        <span style={{ fontSize:"11px", color:CLARO_MUTED }}>— editar o eliminar registros existentes</span>
      </div>

      {/* Selector vista */}
      <div style={{ display:"flex", gap:"8px", marginBottom:"16px" }}>
        {[
          { key:"priorizacion",  label:"📋 Priorización" },
          { key:"certificacion", label:"✅ Certificación" },
        ].map(({ key, label }) => (
          <button key={key} onClick={()=>{ setVista(key); setFiltroMes(""); setFiltroTipo(""); setBusqueda(""); }}
            style={{ padding:"8px 18px", borderRadius:"8px", fontSize:"13px", fontWeight:"600",
              border:"none", cursor:"pointer", transition:"all 0.15s",
              background: vista===key ? CLARO_RED : CLARO_BG,
              color:       vista===key ? "white"   : CLARO_MUTED,
            }}>
            {label} {vista===key && filas.length > 0 && <span style={{ marginLeft:"4px", background:"rgba(255,255,255,0.3)", borderRadius:"10px", padding:"1px 6px", fontSize:"10px" }}>{filas.length}</span>}
          </button>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", marginBottom:"14px", alignItems:"center" }}>
        <input placeholder="🔍 Buscar nodo..." value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          style={{ padding:"7px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", outline:"none", minWidth:"180px" }}
          onFocus={e=>e.target.style.borderColor=CLARO_RED} onBlur={e=>e.target.style.borderColor="#e2e8f0"} />
        <select value={filtroMes} onChange={e=>setFiltroMes(e.target.value)}
          style={{ padding:"7px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", background:"white", cursor:"pointer" }}>
          <option value="">Todos los meses</option>
          {MESES.map(m => <option key={m.value} value={m.value.slice(0,7)}>{m.label}</option>)}
        </select>
        {vista === "priorizacion" && (
          <select value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)}
            style={{ padding:"7px 12px", border:"1.5px solid #e2e8f0", borderRadius:"8px", fontSize:"12px", background:"white", cursor:"pointer" }}>
            <option value="">Todos los tipos</option>
            <option value="Priorizado">🔵 Priorizado</option>
            <option value="Garantia">🟡 Garantía</option>
          </select>
        )}
        {(busqueda || filtroMes || filtroTipo) && (
          <button onClick={()=>{ setBusqueda(""); setFiltroMes(""); setFiltroTipo(""); }}
            style={{ padding:"7px 12px", borderRadius:"8px", fontSize:"11px", fontWeight:"700",
              border:`1.5px solid ${CLARO_RED}`, background:"#f8fafc", cursor:"pointer", color:CLARO_RED }}>
            ✕ Limpiar
          </button>
        )}
        <span style={{ fontSize:"11px", color:CLARO_LIGHT, marginLeft:"auto" }}>{filas.length} registros</span>
      </div>

      {/* Barra acciones selección */}
      {seleccionados.length > 0 && (
        <div style={{ display:"flex", alignItems:"center", gap:"10px", marginBottom:"12px",
          padding:"10px 16px", background:"#fef2f2", borderRadius:"8px", border:"1px solid #fca5a5" }}>
          <span style={{ fontSize:"13px", fontWeight:"700", color:"#991b1b" }}>
            {seleccionados.length} registro(s) seleccionado(s)
          </span>
          <button onClick={handleEliminar} disabled={eliminando}
            style={{ padding:"7px 16px", background: eliminando?"#9ca3af":CLARO_RED, color:"white",
              border:"none", borderRadius:"8px", fontSize:"12px", fontWeight:"700",
              cursor: eliminando?"not-allowed":"pointer" }}>
            {eliminando ? "⏳ Eliminando..." : "🗑 Eliminar seleccionados"}
          </button>
          <button onClick={()=>setSeleccionados([])}
            style={{ padding:"7px 12px", background:"white", border:"1px solid #e2e8f0",
              borderRadius:"8px", fontSize:"12px", cursor:"pointer", color:CLARO_MUTED }}>
            Cancelar
          </button>
        </div>
      )}

      {/* Tabla */}
      <div style={{ overflowX:"auto", borderRadius:"8px", border:"1px solid #e2e8f0" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:"12px" }}>
          <thead>
            <tr>
              <th style={{...thSt, width:"36px"}}>
                <input type="checkbox" checked={seleccionados.length===filas.length && filas.length>0}
                  onChange={toggleTodos} style={{ cursor:"pointer" }} />
              </th>
              {vista === "priorizacion" ? <>
                <th style={thSt}>Nodo</th>
                <th style={thSt}>Nombre / Ciudad</th>
                <th style={thSt}>Mes prio.</th>
                <th style={thSt}>Calidad ICR</th>
                <th style={thSt}>HHPP</th>
                <th style={thSt}>Tipo</th>
                <th style={thSt}>Aliado</th>
                <th style={thSt}>Acciones</th>
              </> : <>
                <th style={thSt}>Nodo</th>
                <th style={thSt}>Nombre / Aliado</th>
                <th style={thSt}>Fecha cert.</th>
                <th style={thSt}>OT / CHG</th>
                <th style={thSt}>Prioridad</th>
                <th style={thSt}>Acciones</th>
              </>}
            </tr>
          </thead>
          <tbody>
            {cargando ? (
              <tr><td colSpan="9" style={{ padding:"32px", textAlign:"center", color:CLARO_MUTED }}>Cargando...</td></tr>
            ) : filas.length === 0 ? (
              <tr><td colSpan="9" style={{ padding:"32px", textAlign:"center", color:CLARO_LIGHT }}>No hay registros.</td></tr>
            ) : filas.map(f => {
              const esSel  = seleccionados.includes(f.id);
              const esEdit = editando?.id === f.id;
              return (
                <tr key={f.id} style={{ background: esSel ? "#fef2f2" : esEdit ? "#f0fdf4" : "white",
                  borderLeft: esSel ? `3px solid ${CLARO_RED}` : esEdit ? "3px solid #16a34a" : "3px solid transparent" }}>
                  <td style={tdSt}>
                    <input type="checkbox" checked={esSel} onChange={()=>toggleSel(f.id)} style={{ cursor:"pointer" }} />
                  </td>
                  {vista === "priorizacion" ? <>
                    <td style={{...tdSt, fontWeight:"700"}}>{f.nodo}</td>
                    <td style={{...tdSt, color:CLARO_MUTED}}>{f.nombre_nodo}<br/><span style={{fontSize:"10px"}}>{f.ciudad}</span></td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input type="month" value={editando.mes_priorizacion||f.mes}
                            onChange={e=>setEditando({...editando, mes_priorizacion:e.target.value})}
                            style={{ padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : f.mes}
                    </td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input type="number" value={editando.priorizacion_calidad??f.priorizacion_calidad}
                            onChange={e=>setEditando({...editando, priorizacion_calidad:e.target.value})}
                            style={{ width:"70px", padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : f.priorizacion_calidad}
                    </td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input type="number" value={editando.hhpp??f.hhpp}
                            onChange={e=>setEditando({...editando, hhpp:e.target.value})}
                            style={{ width:"80px", padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : f.hhpp}
                    </td>
                    <td style={tdSt}>
                      {esEdit
                        ? <select value={editando.tipo||f.tipo} onChange={e=>setEditando({...editando, tipo:e.target.value})}
                            style={{ padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }}>
                            <option value="Priorizado">🔵 Priorizado</option>
                            <option value="Garantia">🟡 Garantía</option>
                          </select>
                        : <span style={{ fontSize:"11px", fontWeight:"700", padding:"2px 8px", borderRadius:"12px",
                            background: f.tipo==="Garantia"?"#fefce8":"#eff6ff",
                            color:       f.tipo==="Garantia"?"#854d0e":"#1d4ed8" }}>
                            {f.tipo==="Garantia"?"🟡 Garantía":"🔵 Priorizado"}
                          </span>}
                    </td>
                    <td style={{...tdSt, color:CLARO_MUTED, fontSize:"11px"}}>{f.aliado}</td>
                  </> : <>
                    <td style={{...tdSt, fontWeight:"700"}}>{f.nodo}</td>
                    <td style={{...tdSt, color:CLARO_MUTED}}>{f.nombre_nodo}<br/><span style={{fontSize:"10px"}}>{f.aliado}</span></td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input type="date" value={editando.fecha_certificacion||f.fecha_certificacion}
                            onChange={e=>setEditando({...editando, fecha_certificacion:e.target.value})}
                            style={{ padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : f.fecha_certificacion}
                    </td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input value={editando.ot_chg??f.ot_chg} onChange={e=>setEditando({...editando, ot_chg:e.target.value})}
                            style={{ width:"110px", padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : <span style={{ fontFamily:"monospace", fontSize:"11px" }}>{f.ot_chg||"—"}</span>}
                    </td>
                    <td style={tdSt}>
                      {esEdit
                        ? <input value={editando.prioridad??f.prioridad} onChange={e=>setEditando({...editando, prioridad:e.target.value})}
                            style={{ width:"90px", padding:"4px 8px", border:"1px solid #e2e8f0", borderRadius:"6px", fontSize:"12px" }} />
                        : f.prioridad||"—"}
                    </td>
                  </>}
                  {/* Botones acción */}
                  <td style={tdSt}>
                    {esEdit ? (
                      <div style={{ display:"flex", gap:"6px" }}>
                        <button onClick={handleGuardarEdicion}
                          style={{ padding:"5px 10px", background:"#16a34a", color:"white", border:"none",
                            borderRadius:"6px", fontSize:"11px", fontWeight:"700", cursor:"pointer" }}>
                          ✓ Guardar
                        </button>
                        <button onClick={()=>setEditando(null)}
                          style={{ padding:"5px 10px", background:"#f1f5f9", color:CLARO_MUTED, border:"1px solid #e2e8f0",
                            borderRadius:"6px", fontSize:"11px", cursor:"pointer" }}>
                          ✕
                        </button>
                      </div>
                    ) : (
                      <div style={{ display:"flex", gap:"6px" }}>
                        <button onClick={()=>setEditando({ id:f.id })}
                          style={{ padding:"5px 10px", background:"#eff6ff", color:"#2563eb", border:"1px solid #bfdbfe",
                            borderRadius:"6px", fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                          ✏ Editar
                        </button>
                        <button onClick={()=>{ setSeleccionados([f.id]); }}
                          style={{ padding:"5px 10px", background:"#fef2f2", color:CLARO_RED, border:`1px solid #fca5a5`,
                            borderRadius:"6px", fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                          🗑
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── TAB PAPELERA ─────────────────────────────────────────────────
// Muestra los registros que la carga de Excel eliminó por sincronización
// (porque ya no estaban en el archivo subido), para poder revisarlos o
// restaurarlos si se quitaron por error. Solo visible en Gestión Huella
// (administración).
function TabPapelera({ API, mostrarAlerta, cargarResumen }) {
  const [filas, setFilas]     = useState([]);
  const [cargando, setCargando] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState("");
  const [busqueda, setBusqueda]     = useState("");

  const cargarPapelera = async () => {
    setCargando(true);
    try {
      const params = new URLSearchParams();
      if (filtroTipo) params.set("tipo", filtroTipo);
      if (busqueda)   params.set("busqueda", busqueda);
      const r = await fetch(`${API}/huella-eliminados?${params.toString()}`);
      const data = await r.json();
      setFilas(Array.isArray(data) ? data : []);
    } catch (e) {
      mostrarAlerta("error", "No se pudo cargar la papelera: " + e.message);
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => { cargarPapelera(); /* eslint-disable-next-line */ }, [filtroTipo]);

  const restaurar = async (id) => {
    try {
      const r = await fetch(`${API}/huella-eliminados/${id}/restaurar`, { method: "POST" });
      const data = await r.json();
      if (data.ok) {
        mostrarAlerta("success", data.mensaje);
        cargarPapelera();
        cargarResumen();
      } else {
        mostrarAlerta("error", data.mensaje);
      }
    } catch (e) { mostrarAlerta("error", e.message); }
  };

  const borrarDefinitivo = async (id) => {
    if (!window.confirm("Esto borra el registro de la papelera para siempre (ya no se podrá restaurar). ¿Continuar?")) return;
    try {
      const r = await fetch(`${API}/huella-eliminados/${id}`, { method: "DELETE" });
      const data = await r.json();
      if (data.ok) { mostrarAlerta("success", data.mensaje); cargarPapelera(); }
      else          mostrarAlerta("error", data.mensaje);
    } catch (e) { mostrarAlerta("error", e.message); }
  };

  const thSt = { padding:"9px 12px", fontSize:"11px", fontWeight:"700", color:CLARO_MUTED, textTransform:"uppercase", letterSpacing:"0.05em", textAlign:"left", borderBottom:"2px solid #e2e8f0" };
  const tdSt = { padding:"9px 12px", fontSize:"12px", color:"#0f172a", borderBottom:"1px solid #f1f5f9" };

  const filasFiltradas = busqueda
    ? filas.filter(f => (f.nodo || "").toLowerCase().includes(busqueda.toLowerCase()))
    : filas;

  return (
    <div style={{ background:"white", borderRadius:"12px", border:"1px solid #e2e8f0", padding:"20px 24px" }}>
      <div style={{ display:"flex", alignItems:"center", gap:"8px", marginBottom:"8px" }}>
        <div style={{ width:"4px", height:"18px", background:CLARO_RED, borderRadius:"2px" }} />
        <div style={{ fontSize:"14px", fontWeight:"700", color:CLARO_DARK }}>Papelera</div>
        <span style={{ fontSize:"11px", color:CLARO_MUTED }}>— registros que la carga de Excel quitó por no estar en el archivo (puedes restaurarlos)</span>
      </div>

      <div style={{ display:"flex", gap:"8px", flexWrap:"wrap", margin:"14px 0", alignItems:"center" }}>
        <select value={filtroTipo} onChange={e=>setFiltroTipo(e.target.value)}
          style={{ padding:"7px 10px", borderRadius:"7px", border:"1px solid #e2e8f0", fontSize:"12px" }}>
          <option value="">Todos los tipos</option>
          <option value="Priorizacion">Priorización</option>
          <option value="Certificacion">Certificación</option>
          <option value="Meta">Metas</option>
        </select>
        <input placeholder="Buscar por nodo..." value={busqueda} onChange={e=>setBusqueda(e.target.value)}
          style={{ padding:"7px 10px", borderRadius:"7px", border:"1px solid #e2e8f0", fontSize:"12px", minWidth:"180px" }} />
        <span style={{ fontSize:"12px", color:CLARO_MUTED }}>{filasFiltradas.length} registro(s) en la papelera</span>
      </div>

      {cargando ? (
        <div style={{ padding:"30px", textAlign:"center", color:CLARO_MUTED, fontSize:"13px" }}>Cargando...</div>
      ) : filasFiltradas.length === 0 ? (
        <div style={{ padding:"30px", textAlign:"center", color:CLARO_MUTED, fontSize:"13px" }}>No hay registros en la papelera.</div>
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse" }}>
            <thead>
              <tr>
                <th style={thSt}>Tipo</th>
                <th style={thSt}>Nodo</th>
                <th style={thSt}>Detalle</th>
                <th style={thSt}>Eliminado el</th>
                <th style={thSt}></th>
              </tr>
            </thead>
            <tbody>
              {filasFiltradas.map(f => (
                <tr key={f.id}>
                  <td style={tdSt}>
                    {f.tipo_registro === "Priorizacion" && "📋 Priorización"}
                    {f.tipo_registro === "Certificacion" && "✅ Certificación"}
                    {f.tipo_registro === "Meta" && "🎯 Meta"}
                  </td>
                  <td style={tdSt}>{f.nodo || f.aliado || "—"}</td>
                  <td style={tdSt}>
                    {f.tipo_registro === "Priorizacion" && `Mes: ${String(f.mes_priorizacion||"").slice(0,10)} · Calidad: ${f.priorizacion_calidad ?? "—"} · HHPP: ${f.hhpp ?? "—"} · ${f.tipo || ""}`}
                    {f.tipo_registro === "Certificacion" && `Fecha cert: ${String(f.fecha_certificacion||"").slice(0,10)} · OT/CHG: ${f.ot_chg || "—"} · Prioridad: ${f.prioridad || "—"}`}
                    {f.tipo_registro === "Meta" && `Mes: ${String(f.mes_priorizacion||"").slice(0,10)} · Meta: ${f.meta ?? "—"}`}
                  </td>
                  <td style={tdSt}>{f.fecha_eliminacion ? new Date(f.fecha_eliminacion).toLocaleString("es-CO") : "—"}</td>
                  <td style={tdSt}>
                    <div style={{ display:"flex", gap:"6px" }}>
                      <button onClick={()=>restaurar(f.id)}
                        style={{ padding:"5px 10px", background:"#ecfdf5", color:VERDE, border:"1px solid #86efac",
                          borderRadius:"6px", fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                        ↩ Restaurar
                      </button>
                      <button onClick={()=>borrarDefinitivo(f.id)}
                        style={{ padding:"5px 10px", background:"#fef2f2", color:CLARO_RED, border:"1px solid #fca5a5",
                          borderRadius:"6px", fontSize:"11px", fontWeight:"600", cursor:"pointer" }}>
                        🗑 Borrar definitivo
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GestionHuella;